import { unwrapConnection } from "@utils/utils";
import React, { ReactNode } from "react";

import { Label, Paragraph } from "@fronterahealth/frontera-ui-components";

import { ProgrammingGoalOverviewQuery, useProgrammingGoalOverviewQuery } from "@api/graphql/types-and-hooks";
import { convertReadableString } from "@components/forms/utils";
import { ProgrammingGoalChart } from "@pages/ClientProgramming/ProgrammingGoalOverview/ProgrammingGoalChart";
import { useClientProgrammingData } from "@providers/ClientProgrammingProvider";

interface ProgrammingGoalOverviewProps {}

// Convert camelCase to "Title Case" (e.g., "teachingMethod" → "Teaching Method")
const camelCaseToReadable = (str: string): string => {
  return str
    .replace(/([A-Z])/g, " $1") // Add space before capital letters
    .replace(/^./, (char) => char.toUpperCase()) // Capitalize first letter
    .trim();
};

const LabeledValue = ({ label, value }: { label: ReactNode; value: ReactNode | null | undefined }) => {
  return (
    <div className="flex flex-col gap-2">
      <div className="whitespace-nowrap">
        <Label size="small">{label}</Label>
      </div>
      <div className="">
        <Paragraph size="small" colorType="primary" className="whitespace-pre-wrap">
          {value || "––"}
        </Paragraph>
      </div>
    </div>
  );
};

// Generic display for EHR details key-value pairs
const EhrDetails = ({ details }: { details: Record<string, string | null | undefined> }) => {
  if (!details || Object.keys(details).length === 0) return null;

  const filteredDetails = Object.entries(details).filter(([, value]) => value);

  if (filteredDetails.length === 0) return null;

  return (
    <div className="flex flex-col gap-4">
      {filteredDetails.map(([key, value]) => {
        return <LabeledValue key={key} label={camelCaseToReadable(key)} value={value} />;
      })}
    </div>
  );
};

// Helper to build mastery criteria string from Passage target fields
const buildMasteryCriteria = (target: {
  measureType?: string;
  measureValue?: number;
  masteredOverType?: string;
  masteredOverValue?: number;
  threshold?: string;
}) => {
  const parts = [];
  if (target.measureType && target.measureValue) {
    parts.push(`${target.measureValue}% ${convertReadableString(target.measureType)}`);
  }
  if (target.masteredOverType && target.masteredOverValue) {
    parts.push(`over ${target.masteredOverValue} ${convertReadableString(target.masteredOverType)}`);
  }
  if (target.threshold) {
    parts.push(`(${target.threshold})`);
  }
  return parts.length > 0 ? parts.join(" ") : null;
};

type GoalData = NonNullable<ProgrammingGoalOverviewQuery["goal"]>;

interface PassageGoalDetailsProps {
  goal: GoalData;
  targetId: string;
  goalStatus?: string | null; // From context (already fetched)
  targetStatus?: string | null; // From context (already fetched)
}

const PassageGoalDetails = ({ goal, targetId, goalStatus, targetStatus }: PassageGoalDetailsProps) => {
  const passageGoal = goal.passagegoal;
  if (!passageGoal) return null;

  const passageTargets = unwrapConnection(passageGoal.passageTargets);
  const passageTarget = passageTargets?.find((t) => t.target?.id === targetId);

  if (passageTarget) {
    return (
      <div className="flex flex-col p-5 gap-5 w-full bg-white rounded-lg border border-limestone-200">
        <div className="flex flex-row gap-4">
          <div className="flex-1">
            <LabeledValue label="Target Type" value={convertReadableString(passageTarget.type)} />
          </div>
          <div className="flex-1">
            <LabeledValue label="Target Phase" value={convertReadableString(targetStatus)} />
          </div>
        </div>
        <div>
          <LabeledValue label="Target Details" value={passageTarget.description} />
        </div>
        <div>
          <LabeledValue label="Mastery Criteria" value={buildMasteryCriteria(passageTarget)} />
        </div>
      </div>
    );
  }

  // Show goal details
  const collectionType = passageTargets?.[0]?.type || "";
  const ehrDetails = {
    operationalDefinition: passageGoal.operationalDefinition,
    onset: passageGoal.onset,
    offset: passageGoal.offset,
    antecedentStrategy: passageGoal.antecedentStrategy,
    errorCorrectionStrategy: passageGoal.errorCorrectionStrategy,
    errorConsequenceStrategy: passageGoal.errorConsequenceStrategy,
    replacementBehavior: passageGoal.replacementBehavior,
    hypothesizedFunction: passageGoal.hypothesizedFunction,
    teachingMethod: passageGoal.teachingMethod,
    discriminativeStimulus: passageGoal.discriminativeStimulus,
    promptingInstructions: passageGoal.promptingInstructions,
    expectedResponse: passageGoal.expectedResponse,
    reinforcementStrategy: passageGoal.reinforcementStrategy,
  };

  return (
    <div className="flex flex-col p-5 gap-5 w-full bg-white rounded-lg border border-limestone-200">
      <div className="flex flex-row gap-4">
        <div className="flex-1">
          <LabeledValue label="Goal Type" value={convertReadableString(passageGoal.type)} />
        </div>
        <div className="flex-1">
          <LabeledValue label="Goal Phase" value={convertReadableString(goalStatus)} />
        </div>
        <div className="flex-1">
          <LabeledValue label="Data Collection Type" value={convertReadableString(collectionType)} />
        </div>
      </div>
      <div>
        <LabeledValue label="Goal Details" value={passageGoal.description} />
      </div>
      <div>
        <EhrDetails details={ehrDetails} />
      </div>
    </div>
  );
};

interface OverviewDetailsProps {
  goalId: string;
  targetId: string;
  goalStatus?: string | null;
  targetStatus?: string | null;
}

const OverviewDetails = ({ goalId, targetId, goalStatus, targetStatus }: OverviewDetailsProps) => {
  const { data } = useProgrammingGoalOverviewQuery(
    {
      id: goalId,
    },
    { enabled: !!goalId },
  );

  const goal = data?.goal;
  if (!goal) return null;

  // Render Passage goal details (primary EHR type supported)
  if (goal.passagegoal) {
    return <PassageGoalDetails goal={goal} targetId={targetId} goalStatus={goalStatus} targetStatus={targetStatus} />;
  }

  return null;
};

export const ProgrammingGoalOverview: React.FC<ProgrammingGoalOverviewProps> = ({}) => {
  const { selectedGoal, selectedTarget } = useClientProgrammingData();
  return (
    <div className="flex flex-col justify-center items-center w-full py-4 gap-4">
      <div className="p-4 w-full bg-white rounded-lg border border-limestone-200">
        <ProgrammingGoalChart goalId={selectedGoal?.id || ""} targetId={selectedTarget?.id || ""} />
      </div>
      <OverviewDetails
        goalId={selectedGoal?.id || ""}
        targetId={selectedTarget?.id || ""}
        goalStatus={selectedGoal?.status}
        targetStatus={selectedTarget?.status}
      />
    </div>
  );
};
